import type { ClassValue } from 'clsx';
import type { ComponentProps, ElementType, ReactElement } from 'react';
import { cn } from './cn.ts';

/**
 * The shape Base UI gives every part's `className`: a string, or a function of
 * that part's own state (`checked`, `open`, `disabled`, …).
 */
type StateClassName<State> = ClassValue | ((state: State) => string | undefined);

/**
 * `cn`, but it survives Base UI's function-form `className`.
 *
 * `cn` alone cannot be used to merge a part's class list: `clsx` drops values it
 * cannot stringify, so a caller passing `className={(state) => …}` would lose
 * their classes silently — it type-checks and renders, just without the styles.
 * When any input is a function, the merge stays a function and each input is
 * resolved against the state at render time.
 */
export function cx<State>(...inputs: StateClassName<State>[]): string | ((state: State) => string) {
  if (!inputs.some((input) => typeof input === 'function')) {
    return cn(...(inputs as ClassValue[]));
  }
  return (state: State) =>
    cn(...inputs.map((input) => (typeof input === 'function' ? input(state) : input)));
}

/**
 * Pin a Base UI part to the design system's classes while leaving its whole API
 * — `render`, state-aware `className`, every part-specific prop — untouched.
 *
 * Base UI ships ~150 parts across its components; spelling each wrapper out by
 * hand would bury the one line that carries design intent (the class list) in
 * boilerplate. Parts that need more than classes — an icon, a fixed structure —
 * are written out as ordinary components instead of going through here.
 *
 * `defaults` covers the props that are a design decision rather than a caller's
 * choice, such as how far a menu floats off its trigger. Callers still win.
 */
export function styled<C extends ElementType>(
  Part: C,
  base: string,
  defaults?: Partial<ComponentProps<C>>,
) {
  function Styled(props: ComponentProps<C>): ReactElement {
    const { className, ...rest } = props as { className?: StateClassName<unknown> };
    const Component = Part as ElementType;
    return <Component className={cx(base, className)} {...defaults} {...rest} />;
  }

  Styled.displayName = (Part as { displayName?: string }).displayName ?? 'Styled';

  return Styled;
}
